import * as _ from 'underscore';
import { AccordionState } from '../helpers/AccordionState';
import { PermissionsHelper } from '../helpers/PermissionsHelper';
import { L10n } from '../L10n/L10n';
import { ColumnWidths } from '../models/ColumnWidths';
import { IGranteePermissions } from '../models/IGranteePermissions';
import { ITemplate } from '../models/ITemplate';
import { IPermission, Permission, PermissionReason } from '../models/Permission';
import { PermissionRoles } from '../models/PermissionRoles';
import { ExplicitPermissions } from '../resources/ExplicitPermissions';
import { IResult } from '../ServerApi/types/IResponse';
import { ITypedIds } from '../ServerApi/types/ITypedIds';
import { ServerService } from '../services/ServerService';
import { ToasterService, ToastType } from '../services/ToasterService';
import { PermissionsPanelCtrl } from './PermissionsPanelCtrl';

export interface IPermissionRuleEditorScope extends ng.IScope {
  // directive parameters
  updateAuthorizables: () => ng.IPromise<void>;
  hideRowMenu: () => any;
  scrollIntoView: () => any;

  authorizables: ITypedIds;

  action: PermissionRuleEditorCommand;
  rowIndex: number;
  selectedRow: IGranteePermissions;
  columnWidths: any; // ColumnWidths;
  busy: boolean;

  // set by directive or controller
  permissionsEditorTemplates: string;
  cancel: () => any;
  isDeleteActionSelected: () => boolean;
  // the row that we modify and ultimately send to the server (save) or throw away (cancel).
  data: { row: IGranteePermissions };
  getSaveButtonText: () => string;
  savePermissionRule: () => any;
  saveDisabled: () => boolean;
  permissionsPanelCtrl: PermissionsPanelCtrl;
  templates: {
    cells: ITemplate[];
  };
  getTooltipText: ($event: JQueryEventObject) => string;

  roleOptions(contentType: string): any[];
  selectedRoleByContentType: _.Dictionary<string>;

  // Proxy methods -- should probably be moved to a helper class.
  explicitGranteeRole: (row: IGranteePermissions, contentType: string) => string;
  granteeType: (row: IGranteePermissions) => string;
  granteeName: (row: IGranteePermissions) => string;
  capabilityValue: (row: IGranteePermissions, contentType: string, capability: string, defaultValue: string) => string;
  capabilityTitleKey: (row: IGranteePermissions, contentType: string, capability: string, defaultValue: string) => string;

  // the column widths in the editable row are based on the column widths of the underlying permissions panel
  permissionsPanelColumnWidths: ColumnWidths;

  // facade that wraps the call to columnWidths.resizer.continueResize in $scope.apply()
  resize: (xPos: number) => void;

  accordion: AccordionState;

  flowsOnSiteEnabled: boolean;
}

export type PermissionRuleEditorCommand = 'add' | 'edit' | 'delete';

class PermissionEditorTemplateUrl {
  static forAuthorizableType(authorizableType: string): string {
    return authorizableType + 'PermissionsEditor.html';
  }
}

export class PermissionRuleEditorCtrl {
  public static $inject = [
    '$scope',
    '$translate',
    'ExplicitPermissions',
    'PermissionRoles',
    'ServerService',
    'ToasterService'
  ];

  private authorizableType: string;
  private scope: IPermissionRuleEditorScope;
  private explicitPermissions: ExplicitPermissions;
  private permissionRoles: PermissionRoles;
  private $translate: ng.translate.ITranslateService;
  private toaster: ToasterService;

  constructor(
      $scope: IPermissionRuleEditorScope,
      $translate: ng.translate.ITranslateService,
      explicitPermissions: ExplicitPermissions,
      permissionRoles: PermissionRoles,
      server: ServerService,
      toaster: ToasterService
  ) {
    this.scope = $scope;
    this.$translate = $translate;
    this.explicitPermissions = explicitPermissions;
    this.permissionRoles = permissionRoles;
    this.toaster = toaster;

    this.scope.flowsOnSiteEnabled = server.flowsOnSiteEnabled();
    this.scope.explicitGranteeRole = (row, contentType) =>
        PermissionsHelper.explicitGranteeRole(row, contentType, this.permissionRoles);
    this.scope.granteeType = PermissionsHelper.granteeType;
    this.scope.granteeName = PermissionsHelper.granteeName;
    this.scope.capabilityValue = PermissionsHelper.capabilityValue;
    this.scope.capabilityTitleKey = PermissionsHelper.capabilityTitleKey;
    this.scope.busy = false;
    this.updateColumnWidths();
    this.authorizableType = $scope.authorizables.type;
    this.scope.permissionsEditorTemplates = PermissionEditorTemplateUrl.forAuthorizableType($scope.authorizables.type);
    // The row needs to be nested inside the data object so that when a different row is selected, the new row is
    // seen by the editor templates. This is because tbTranscludeFn doesn't watch tbTranscludeScope for new data.
    this.scope.data = { row: null };
    this.initializeFromSelectedRow();

    this.scope.resize = (xPos: number) => {
      $scope.$apply(() => {
        $scope.columnWidths.resizer.continueResize(xPos);
      });
    };
  }

  public initializeFromSelectedRow() {
    // Clone the row. The editor never changes the original row. It builds a new one and saves it to the server.
    this.scope.data.row = this.deepCopy(this.scope.selectedRow);
    this.scope.selectedRoleByContentType = {}; // key: authorizable type, value: role

    // Each content type has it's own list of available roles
    _.each(_.keys(this.scope.data.row.permissionsByContentType),
      (contentType: string) =>
        this.scope.selectedRoleByContentType[contentType] = PermissionsHelper.explicitGranteeRole(this.scope.data.row, contentType, this.permissionRoles));
  }

  public getTooltipText($event: JQueryEventObject): string {
    return $($event.target)
      .closest('.tb-capability-value-cell')
      .find('.tb-capability-value')
      .attr('data-tooltip');
  }

  public savePermissionRule() {
    const granteePermissions = this.scope.data.row;
    this.scope.busy = true;

    // clone the permissions so that any changes aren't rendered in the UI while saving.
    let newPermissionsByContentType: _.Dictionary<_.Dictionary<IPermission>> = {};
    const newGranteePermissions = _.clone(granteePermissions);

    if(this.scope.action === 'delete') {
      newPermissionsByContentType = this.cloneAllPermissionsToUnspecified(granteePermissions.permissionsByContentType);
    } else {
      // It would be incorrect to send 'mixed' permissions to the server, so 'mixed' permissions are not cloned.
      // Unchanged permissions are not cloned because only changed permissions are sent to the server.
      _.each(granteePermissions.permissionsByContentType, (permissions: _.Dictionary<IPermission>, contentType: string) => {
        newPermissionsByContentType[contentType] = {};
        _.each(permissions, (permission: IPermission, capability: string) => {
          if (permission.permission !== Permission.Mixed && permission.permission !== this.scope.selectedRow.permissionsByContentType[contentType][capability].permission) {
            newPermissionsByContentType[contentType][capability] = permission;
          }
        });
      });
    }
    newGranteePermissions.permissionsByContentType = newPermissionsByContentType;

    const successToastStringId = this.isAuthorizableCatalogType(this.authorizableType)
      ? 'permissionRuleChangedSuccessWithWait'
      : 'permissionRuleChangedSuccess';

    this.explicitPermissions.setForAuthorizables(this.scope.authorizables, newGranteePermissions)
      .then((result: IResult) => {
        this.scope.updateAuthorizables().then(() => {
          this.scope.hideRowMenu();
          if (result.errors && result.errors.length) {
            this.toaster.pop({
              type: ToastType.Error,
              text: this.$translate.instant('permissionRuleChangeFailed', {
                GRANTEE_NAME: L10n.elideIfLong(this.scope.granteeName(granteePermissions))
              })
            });
          } else {
            this.toaster.pop({
              type: ToastType.Info,
              text: this.$translate.instant(successToastStringId, {
                GRANTEE_NAME: L10n.elideIfLong(this.scope.granteeName(granteePermissions))
              })
            });
          }
        });
      })
      .finally(() => this.scope.busy = false);
  }

  /**
   *  Creates a copy of the permissionsByContentType dictionary which has all permissions set to unspecified. This is used to delete a permission rule.
   *  TODO: push this logic into a delete permission rule method in the ExplicitPermisions resource.
   */
  private cloneAllPermissionsToUnspecified(permissionsByContentType: _.Dictionary<_.Dictionary<IPermission>>): _.Dictionary<_.Dictionary<IPermission>> {
    return _.reduce(permissionsByContentType,
      (permissionsByContentType: _.Dictionary<_.Dictionary<IPermission>>, value: _.Dictionary<IPermission>, index: any) => {
        permissionsByContentType[index] = _.reduce(value,
          (permissions: _.Dictionary<IPermission>, value: IPermission, index: any) => {
            permissions[index] = {
              permission: Permission.Unspecified,
              reason: PermissionReason.Unspecified
            };
            return permissions;
          }, <_.Dictionary<IPermission>>{});
        return permissionsByContentType;
      }, <_.Dictionary<_.Dictionary<IPermission>>>{});
  }

  public saveDisabled(): boolean {
    return !this.scope.isDeleteActionSelected() && _.isEqual(this.scope.data.row, this.scope.selectedRow);
  }

  public getCapabilitiesForTypeAndRole(contentType: string, role: string): _.Dictionary<IPermission> {
    const permissions = this.permissionRoles.getPermissionsForTypeAndRole(contentType, role);
    return PermissionsHelper.IPermissionsFromPermissions(permissions);
  }

  private updateColumnWidths(): any {
    this.scope.columnWidths = this.scope.permissionsPanelColumnWidths;
  }

  private deepCopy(row: IGranteePermissions): IGranteePermissions {
    return jQuery.extend(true, {}, row);
  }

  private isAuthorizableCatalogType(authorizableType: string) {
    return (authorizableType === 'table' || authorizableType === 'database');
  }
}
